import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ICompany } from 'src/app/@interfaces/company.interface';
import { IRol } from 'src/app/@interfaces/rol.interface';
import { CompanyService } from 'src/app/@pages/company/company.service';
import { UsersService } from 'src/app/@pages/users/users.service';
import { ValidatorsService } from 'src/app/@shared/validator/validators.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IDocumenType } from 'src/app/@interfaces/authentication/documenType';
import { ILoginResponse } from 'src/app/@interfaces/authentication/login-response.interface';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import { IPrefix } from 'src/app/@interfaces/prefix.interface';
import { AlertsService } from 'src/app/@shared/components/services/alerts/alerts.service';
import { AlertFeedbackService } from '@shared-services';
import { GraphQLErrorResponse } from '@shared-models';
import { TranslatePipe } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { STRING_CONSTANTS } from '@shared-constants';

@Component({
  selector: 'app-register-user',
  templateUrl: './register-user.component.html',
  styleUrls: ['./register-user.component.css'],
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    MatButtonModule,
    TranslatePipe,
  ],
})
export class RegisterUserComponent implements OnInit, OnDestroy {
  rolList: Array<IRol> = [];
  companyList: Array<ICompany> = [];
  registerForm: UntypedFormGroup = this.fb.group(
    {
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(this.validatorS.nombrePattern),
        ],
      ],
      types_docs: ['', Validators.required],
      document: [
        '',
        [
          Validators.required,
          Validators.pattern(this.validatorS.documentPattern),
        ],
      ],
      email: ['', [Validators.required, this.validatorS.emailValidator]],
      password: [
        '',
        [
          Validators.required,
          Validators.pattern(this.validatorS.passwordPattern),
        ],
        [],
      ],
      passwordConfirmation: ['', Validators.required],
      rol: ['', Validators.required],
      company: ['', Validators.required],
      prefix: [''],
    },
    {
      validators: [
        this.validatorS.equalFields('password', 'passwordConfirmation'),
      ],
    },
  );
  companyId = '';
  unsubscribe$ = new Subject<void>();
  prefixList: IPrefix[] = [];
  documenTypeList: Array<IDocumenType> = [];

  constructor(
    private fb: UntypedFormBuilder,
    private validatorS: ValidatorsService,
    private companyS: CompanyService,
    private dialog: MatDialog,
    private userService: UsersService,
    private cookieAuthService: CookieAuthService,
    private alertsService: AlertsService,
    private alertFeedback: AlertFeedbackService,
  ) {}

  ngOnInit(): void {
    this.getAuthValues();
    this.userService
      .getRoles()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        this.rolList = result;
      });
    this.companyS
      .getAllCompanies()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        this.companyList = result;
      });
    this.userService
      .listDocumenTypes()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        this.documenTypeList = result;
      });
  }

  onOptionSelected() {
    const value = this.registerForm.value.company;
    if (
      value.internal_electronic_invoicing === STRING_CONSTANTS.TRUE &&
      value.countries[0].name === 'Colombia'
    ) {
      this.registerForm.get('prefix')?.setValidators(Validators.required);
      this.registerForm.get('prefix')?.updateValueAndValidity();
      this.prefixList = value.prefixes;
    } else {
      this.registerForm.get('prefix')?.clearValidators();
      this.registerForm.get('prefix')?.updateValueAndValidity();
      this.prefixList = [];
    }
  }

  getAuthValues() {
    const company = this.cookieAuthService.getCompanyObject;
    this.companyId = company!.Id_company!.toString();
  }

  //TODO: Remove this repated code that is in login component as well
  register() {
    if (this.registerForm.invalid === true) {
      this.alertsService.showErrorAlert(
        'Please, fill in all the required fields!',
      );
      return;
    } else {
      this.userService
        .registerUser(
          parseFloat(this.registerForm.value.document),
          this.registerForm.value.name,
          this.registerForm.value.email,
          this.registerForm.value.password,
          parseInt(this.registerForm.value.rol.Id_rol),
          parseInt(this.registerForm.value.types_docs.id_docs),
          parseInt(this.registerForm.value.company.Id_company),
          parseInt(this.registerForm.value.prefix.id),
        )
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (result: ILoginResponse) => {
            if (result.token !== 'email' && result.token !== 'document') {
              // Seller registration (user_seller) is handled by the backend when
              // the user is created as an Invoicing Operator. See ticket 86ajc92gq.
              this.alertsService.showSuccessfullAlert(
                'Now you may try starting session',
                'User Created',
              );
              this.registerForm.reset();
              this.alertsService.refresh('/table', '/user');
            } else if (result.token === 'email') {
              this.alertsService.showWarningAlert(
                'Email already exists please try starting session',
              );
            } else if (result.token === 'document') {
              this.alertsService.showWarningAlert(
                'Document already exists please try starting session',
              );
            } else {
              this.alertsService.showErrorAlert(
                'Something happened please check with an administrator',
              );
            }
          },
          error: (err: GraphQLErrorResponse) =>
            this.alertFeedback.errorFromGraphQL(err),
        });
    }
  }

  closeDialog() {
    this.dialog.closeAll();
  }

  textValid(text: string) {
    return (
      this.registerForm.get(text)?.invalid &&
      this.registerForm.get(text)?.touched
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
